import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, LogOut, Trash2 } from 'lucide-react';

import { cn } from '~shared/shadcn/lib/utils';
import { Button } from '~shared/shadcn/ui/button';

import { HelpRail } from './help-rail';
import { ImportSessionIdBar } from './import-session-id-bar';
import { WIZARD_STEPS } from './constants';
import type { SourcePlatform } from '~entities/import-job/api/types';

interface WizardShellProps {
  currentStep: number;
  sourcePlatform: SourcePlatform;
  jobId?: string | null;
  wizardSessionId: string;
  children: ReactNode;
  onSaveExit?: () => void;
  onDiscardDraft?: () => void;
  canDiscardDraft?: boolean;
}

export function WizardShell({
  currentStep,
  sourcePlatform,
  jobId,
  wizardSessionId,
  children,
  onSaveExit,
  onDiscardDraft,
  canDiscardDraft = false,
}: WizardShellProps) {
  return (
    <div className='flex min-h-[calc(100vh-4rem)] flex-col gap-4 p-6'>
      <Link
        to='/dashboard'
        className='flex w-max items-center gap-2 text-muted-foreground transition-colors hover:text-foreground'
      >
        <CircleArrowLeft className='size-5' />
        <span className='text-sm font-medium'>Back to Dashboard</span>
      </Link>
      <ImportSessionIdBar jobId={jobId} wizardSessionId={wizardSessionId} />
      <div className='flex min-h-0 flex-1 gap-6'>
      <aside className='hidden w-56 shrink-0 lg:block'>
        <h1 className='mb-6 text-lg font-semibold'>
          Set up your classes and families
        </h1>
        <nav className='space-y-1'>
          {WIZARD_STEPS.map((step) => (
            <div
              key={step.id}
              className={cn(
                'rounded-md px-3 py-2 text-sm',
                currentStep === step.id
                  ? 'bg-primary/10 font-medium text-primary'
                  : currentStep > step.id
                    ? 'text-muted-foreground'
                    : 'text-muted-foreground/60',
              )}
            >
              <span className='mr-2 tabular-nums'>{step.id + 1}.</span>
              {step.label}
            </div>
          ))}
        </nav>
        {onSaveExit && currentStep < 5 && (
          <Button
            type='button'
            variant='secondary'
            size='sm'
            className='mt-8 w-full justify-start gap-2'
            onClick={onSaveExit}
          >
            <LogOut className='size-4' />
            Save &amp; exit
          </Button>
        )}
        <p className='mt-1 px-1 text-xs text-muted-foreground'>
          Your progress is saved — you can safely leave and resume later.
        </p>
        {canDiscardDraft && onDiscardDraft && currentStep < 5 && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='mt-3 w-full justify-start gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive'
            onClick={onDiscardDraft}
          >
            <Trash2 className='size-4' />
            Discard draft
          </Button>
        )}
        <Link
          to='/import-data-logs'
          className='mt-6 block text-sm text-muted-foreground underline'
        >
          Import history
        </Link>
      </aside>

      <main className='min-w-0 flex-1'>{children}</main>

      <aside className='hidden w-72 shrink-0 xl:block'>
        <HelpRail sourcePlatform={sourcePlatform} />
      </aside>
      </div>
    </div>
  );
}
